import {
	ATTRIBUTES,
	openWaitlistModal,
	PUB_SUB_EVENTS,
	SELECTORS,
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { publish } from '@/scripts/core/global'
import { QuickAddModal } from '@/scripts/optional/quick-add'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	addItemsToCart,
	getCart,
	getDOMCart,
	getDOMCartSectionApiIds,
	hasMatchingBundleInCart,
	hasDomCart,
	openCartDrawerWithOptimisticItem,
	renderResponseToCartDrawer,
	type AddToCartItem,
} from '@/scripts/core/cart-functions'
import {
	getCachedProductByVariantId,
	refreshCartSnapshot,
} from '@/scripts/core/product-cache'
import { CartErrorResponse, CartItem } from '@/scripts/shopify'
import {
	syncInternalAttribFromCookie,
	syncPayLaterCartAttribute,
} from '@/scripts/core/cart-attribution'
import { buildOptimisticCartItems } from '@/scripts/cart/optimistic-cart'
import { CartDrawer } from '@/scripts/cart/cart-drawer'
import { forceReleaseAllLocks, isLocked } from '@/scripts/cart/optimistic-lock'
import { cartDebug, summarizeCartItems } from '@/scripts/cart/optimistic-debug'

export class ProductForm extends UcoastEl {
	static htmlSelector = 'product-form'
	static selectors = {
		form: 'form',
		formIdEl: '[name=id]',
		submitButton: '[type="submit"]',
		submitButtonMessage: '[type="submit"] span',
		soldOutMessage: '[data-uc-sold-out-message]',
		errorMessageWrapper: '[data-uc-product-form-error-message-wrapper]',
		errorMessage: '[data-uc-product-form-error-message]',
	}

	form: HTMLFormElement
	formIdEl: HTMLInputElement
	submitButton: HTMLButtonElement | null
	errorMessageWrapper?: HTMLElement
	errorMessage?: HTMLElement
	hideErrors: boolean
	error?: boolean

	constructor() {
		super()

		this.form = q.rs(ProductForm.selectors.form, this)
		this.formIdEl = q.rs(ProductForm.selectors.formIdEl, this.form)
		this.formIdEl.disabled = false
		this.submitButton =
			q.os<HTMLButtonElement>(ProductForm.selectors.submitButton, this) ??
			null
		if (!this.submitButton) {
			cartDebug('product-form:init:missing-submit-button', {
				hasForm: Boolean(this.form),
				hasIdInput: Boolean(this.formIdEl),
			})
			this.hideErrors = this.dataset.hideErrors === 'true'
			return
		}

		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))

		if (hasDomCart()) {
			this.submitButton.setAttribute('aria-haspopup', 'dialog')
		}

		this.hideErrors = this.dataset.hideErrors === 'true'
	}

	override connectedCallback() {
		super.connectedCallback()
		if (!this.submitButton) return
		// Fallback for products without a variant picker (e.g. single-variant bundles).
		// variant-selects normally owns soldout state, but it doesn't mount when the
		// product has only one variant. Apply soldout state from the button's disabled
		// attribute so the waitlist form shows and buy buttons hide correctly.
		const section = this.closest('section')
		const hasVariantSelects = section
			? q.os('variant-selects, variant-radios', section)
			: false
		if (!hasVariantSelects) {
			const isDisabled =
				this.submitButton.hasAttribute('disabled') ||
				this.submitButton.getAttribute('aria-disabled') === 'true'
			this.setSoldoutState(isDisabled)
		}
	}

	// Toggle soldout container and buy buttons wrapper visibility
	private setSoldoutState(isOutOfStock: boolean) {
		const soldoutEl =
			document.querySelector<HTMLElement>('.product__soldout')
		const buyButtonsWrapper =
			this.closest<HTMLElement>('.add__card-wrapper')

		// Respect server-rendered OOS state — don't hide on initial mount
		if (!isOutOfStock && soldoutEl?.hasAttribute('data-oos-initial')) {
			return
		}
		soldoutEl?.removeAttribute('data-oos-initial')

		if (soldoutEl) {
			soldoutEl.classList.toggle('active', isOutOfStock)
			soldoutEl.style.display = isOutOfStock ? 'block' : 'none'
		}
		if (
			buyButtonsWrapper &&
			!buyButtonsWrapper.hasAttribute('data-sticky-notify-wrapper')
		) {
			buyButtonsWrapper.classList.toggle('hidden', isOutOfStock)
			buyButtonsWrapper.style.display = isOutOfStock ? 'none' : ''
		}
	}

	getSpinner() {
		return q.rs(SELECTORS.loadingOverlaySpinner, this)
	}

	async onSubmitHandler(event: Event) {
		event.preventDefault()
		if (!this.submitButton) return
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return
		const oosId = q.oa(this.submitButton, 'data-oos-popup-trigger')
		if (oosId) {
			openWaitlistModal(parseInt(oosId), this.submitButton)
			return
		}
		this.handleErrorMessage()

		this.submitButton.setAttribute('aria-disabled', 'true')
		this.submitButton.setAttribute(ATTRIBUTES.loading, '')
		this.getSpinner().classList.remove('hidden')

		const formData = new FormData(this.form)
		const payLaterValue = (formData.get('_pay_later') || '')
			.toString()
			.trim()
		let formVariantId = formData.get('id')
		if (!formVariantId) throw Error('No variant id found')
		const addedVariantId = parseInt(formVariantId.toString())

		// Check if the same bundle is already in cart without payLater change
		const currentCart = await getCart()
		const payLaterToCompare = payLaterValue || null
		const isSameBundleInCart =
			'status' in currentCart
				? false
				: hasMatchingBundleInCart(
						currentCart.items,
						addedVariantId,
						payLaterToCompare
					)

		cartDebug('product-form:duplicate-check', {
			variantId: addedVariantId,
			payLaterToCompare,
			isSameBundleInCart,
			cartItems:
				'status' in currentCart
					? []
					: currentCart.items.map((i) => ({
							id: i.id,
							props: i.properties,
						})),
		})

		if (isSameBundleInCart) {
			cartDebug('product-form:same-bundle-skip', {
				variantId: addedVariantId,
			})
			// Just open the cart drawer without making any request
			const cartEl = getDOMCart()
			if (
				cartEl instanceof CartDrawer &&
				!cartEl.classList.contains('active')
			) {
				cartEl.open()
			}
			// Reset button state
			this.getSpinner().classList.add('hidden')
			this.submitButton.removeAttribute(ATTRIBUTES.loading)
			this.submitButton.removeAttribute('aria-disabled')
			return
		}

		// Build AddToCartItem from form data (avoids FormData round-tripping)
		const mainItem = this.extractItemFromFormData(formData, addedVariantId)
		const purchaseAsGift =
			String(
				mainItem.properties?._purchase_as_gift ?? ''
			).toLowerCase() === 'true'
		cartDebug('product-form:submit', {
			variantId: addedVariantId,
			item: summarizeCartItems([mainItem])[0],
			payLater: payLaterValue || null,
			purchaseAsGift,
		})

		try {
			// Build optimistic data from cached product (skeleton fallback if cache-cold)
			const product = getCachedProductByVariantId(addedVariantId)
			const optimisticData = buildOptimisticCartItems({
				variantId: addedVariantId,
				product: product ?? undefined,
				quantity: mainItem.quantity,
				payLater: payLaterValue || null,
				purchaseAsGift,
				isTrial:
					String(
						mainItem.properties?._is_trial ?? ''
					).toLowerCase() === 'true',
			})

			const hasExclusiveAssociated = optimisticData.associated.some(
				(item) =>
					item.type === 'bundle_membership' ||
					item.type === 'alcm'
			)
			const shouldResolveExclusiveConflicts =
				optimisticData.main.isExclusive || hasExclusiveAssociated
			const cartEl = getDOMCart()
			if (!(cartEl instanceof CartDrawer))
				throw new Error('No cart drawer')

			cartDebug('product-form:optimistic-start', {
				mainVariantId: optimisticData.main.variantId,
				mainIsExclusive: optimisticData.main.isExclusive,
				mainIsSkeleton: optimisticData.main.isSkeleton ?? false,
				shouldResolveExclusiveConflicts,
				associated: optimisticData.associated.map((a) => ({
					type: a.type,
					variantId: a.variantId,
					title: a.title,
				})),
			})
			// T=0: Show main product + associated items (memberships/GWP)
			// for instant visual feedback. Falls back to skeleton row when
			// product cache is cold. Server re-render replaces atomically.
			openCartDrawerWithOptimisticItem(
				optimisticData.main,
				optimisticData.associated
			)

			// T=0: Acquire lock (disables checkout, registers finalRender)
			const lockToken =
				cartEl.acquireOptimisticLock('product-form-add')

			// Close quick-add modal immediately if present
			const quickAddModal = q.oc<QuickAddModal>(
				this,
				'quick-add-modal'
			)
			if (quickAddModal) quickAddModal.hide(true)

			try {
				const cart = await addItemsToCart(
					[mainItem],
					getDOMCartSectionApiIds(),
					{
						conflictMode: shouldResolveExclusiveConflicts
							? 'exclusive_only'
							: 'none',
					}
				)
				cartDebug('product-form:addItemsToCart-response', {
					hasError: 'status' in cart,
					hasSections:
						'sections' in cart && Boolean(cart.sections),
					itemCount:
						'items' in cart ? cart.items.length : undefined,
				})

				if ('status' in cart) {
					forceReleaseAllLocks()
					void cartEl.forceRefreshFromServer()
					this.handleError(cart, addedVariantId)
					return
				}

				if (!('sections' in cart) || !cart.sections) {
					forceReleaseAllLocks()
					await renderResponseToCartDrawer(cart)
					return
				}

				// When exclusive conflicts are resolved, the cart state on server
				// changes but the initial /cart/add.js response sections are stale
				// (rendered before server-side update-instructions ran). Strip the
				// sections so handleServerResponse fetches fresh ones instead of
				// rendering stale HTML that still shows evicted items.
				if (cart._conflictsResolved) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					delete (cart as any).sections
				}

				// Hand off to lock-aware rendering pipeline
				cartEl.handleServerResponse(cart, lockToken)
				cartDebug('product-form:handleServerResponse', {
					lockToken,
				})

				// Non-cart-endpoint side effects can fire immediately
				publish(PUB_SUB_EVENTS.cartUpdate, {
					source: 'product-form',
					productVariantId: addedVariantId,
				})
				this.trackProductAdded(cart)

				// Cart-endpoint side effects MUST wait until the update
				// cycle finishes — otherwise they compete for Shopify's
				// session-locked cart endpoint and can cause the update
				// cycle to fail or return stale data.
				const runCartSideEffects = () => {
					refreshCartSnapshot()
					void Promise.all([
						syncPayLaterCartAttribute(payLaterValue || null),
						syncInternalAttribFromCookie(),
					]).catch((e) =>
						console.error(
							'[ProductForm] Attribution sync error:',
							e
						)
					)
				}
				if (!isLocked()) {
					runCartSideEffects()
				} else {
					const poll = setInterval(() => {
						if (!isLocked()) {
							clearInterval(poll)
							runCartSideEffects()
						}
					}, 200)
				}
			} catch (e) {
				cartDebug('product-form:optimistic-failed', {
					error: e instanceof Error ? e.message : String(e),
				})
				console.error('[ProductForm] Optimistic add failed:', e)
				await cartEl.forceRefreshFromServer()
			}
		} catch (e) {
			console.error(e)
		} finally {
			if (!this.submitButton) return
			this.submitButton.removeAttribute(ATTRIBUTES.loading)

			if (!this.error) this.submitButton.removeAttribute('aria-disabled')
			this.getSpinner().classList.add('hidden')
		}
	}

	private static IGNORED_FORM_KEYS = new Set([
		'id',
		'quantity',
		'form_type',
		'selling_plan',
		'utf8',
		'product-id',
		'section-id',
	])

	/**
	 * Extract an AddToCartItem from the form's FormData.
	 * Includes trial metafields in properties so we can send an item array
	 * instead of raw FormData (enables batching predicted additions).
	 */
	private extractItemFromFormData(
		formData: FormData,
		variantId: number
	): AddToCartItem {
		const properties: Record<string, string> = {}
		for (const [key, value] of formData.entries()) {
			if (value instanceof File) continue
			if (
				ProductForm.IGNORED_FORM_KEYS.has(key) ||
				key.includes('options[')
			)
				continue
			let parsed: unknown
			try {
				parsed = JSON.parse(value as string)
			} catch {
				parsed = value
			}
			if (parsed != null && parsed !== '') {
				properties[key] =
					typeof parsed === 'string' ? parsed : String(parsed)
			}
		}

		// Apply trial metafields
		const trialMeta = this.getTrialMetafields(variantId)
		Object.assign(properties, trialMeta)

		const item: AddToCartItem = {
			id: variantId,
			quantity: parseInt(formData.get('quantity')?.toString() || '1'),
			properties,
		}

		const sellingPlan = formData.get('selling_plan')
		if (sellingPlan) {
			const spId = parseInt(sellingPlan.toString())
			if (!isNaN(spId) && spId > 0) item.selling_plan = spId
		}

		return item
	}

	private getTrialMetafields(variantId: number): Record<string, string> {
		const raw = this.dataset.trialMetafields
		if (!raw) return {}
		try {
			const trialMap: Record<string, Record<string, string>> = JSON.parse(
				raw
			)
			const data = trialMap[variantId.toString()]
			if (!data) return {}
			const result: Record<string, string> = {}
			for (const [key, value] of Object.entries(data)) {
				if (value != null && value !== '') result[key] = String(value)
			}
			return result
		} catch {
			return {}
		}
	}
	/**
	 * Track "Product Added" Segment event after successful cart add
	 */
	private trackProductAdded(cart: { items: CartItem[] }) {
		try {
			if (!window.SegmentUser) return

			for (const item of cart.items) {
				void window.SegmentUser.sendSegmentEvent(null, {
					eventName: 'Product Added',
					sent_from: 'Pvolve Web Script',
					brand: item.vendor,
					category: item.product_type,
					url: window.location.origin + item.url,
					product_id: item.product_id,
					sku: item.sku,
					name: item.product_title,
					price: item.final_price / 100,
					quantity: item.quantity,
					variant: item.variant_title || '',
					shopify_product_id: item.product_id,
					shopify_variant_id: item.id,
					currency: 'USD',
					image_url: item.image,
				})
			}
		} catch (e) {
			console.error('[Segment] Error tracking Product Added:', e)
		}
	}

	handleError(cart: CartErrorResponse, addedVariantId: number) {
		publish(PUB_SUB_EVENTS.cartError, {
			source: 'product-form',
			productVariantId: addedVariantId,
			message: cart.message,
			errors: cart.errors ?? cart.description,
		})
		this.handleErrorMessage(cart.description)
		if (!this.submitButton) return

		const soldOutMessage = this.submitButton.querySelector(
			ProductForm.selectors.soldOutMessage
		)
		if (!soldOutMessage) return
		this.submitButton.setAttribute('aria-disabled', 'true')
		const submitButtonText = q.rs(
			ProductForm.selectors.submitButtonMessage,
			this.submitButton
		)
		submitButtonText.classList.add('hidden')
		soldOutMessage.classList.remove('hidden')
	}

	handleErrorMessage(errorMessage?: string) {
		if (this.hideErrors) return

		this.errorMessageWrapper =
			this.errorMessageWrapper ||
			q.rs(ProductForm.selectors.errorMessageWrapper, this)
		if (!this.errorMessageWrapper)
			throw new Error('No error message wrapper found')
		this.errorMessage =
			this.errorMessage ||
			q.rs(ProductForm.selectors.errorMessage, this.errorMessageWrapper)

		this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage)

		if (errorMessage) {
			this.errorMessage.textContent = errorMessage
		}
	}
}
